/**
 * Whether the player wants music, remembered across sessions.
 *
 * The key is duplicated verbatim in `frontend/landing/index.html`'s inline
 * script. That is deliberate: the landing page is a standalone static file that
 * must keep working when dropped on any host with no bundler (see the `landing`
 * plugin in `vite.config.ts`), so it cannot import this module. Same origin and
 * same key means muting on the landing page is still muted after logging in,
 * which is the behaviour a player expects. **If this string changes, change it
 * there too** -- the landing test guards that pairing.
 */
export const MUSIC_ENABLED_STORAGE_KEY = 'tallow.audio.music';

/**
 * Has the player made an explicit choice, and what was it?
 *
 * `null` means "never touched the control", and the two screens that play music
 * deliberately resolve that differently:
 *
 * - **Hub -> on.** A game hub with a music bed is what a player expects, and
 *   there is a visible toggle in the header the moment they want it gone.
 * - **Landing -> off.** A marketing page that starts playing music at a
 *   first-time visitor is hostile, and the visitor has no relationship with us
 *   yet to spend on it. The control invites the music instead.
 *
 * Once the player *does* choose, both honour it — so turning music off on the
 * landing page means the hub is quiet too.
 *
 * Reads defensively: Safari private mode throws on `localStorage` access rather
 * than returning null, and a thrown getter here would take down whatever screen
 * is mounting the bed.
 */
export function readMusicPreference(): boolean | null {
  try {
    const stored = window.localStorage.getItem(MUSIC_ENABLED_STORAGE_KEY);
    if (stored === 'on') return true;
    if (stored === 'off') return false;
    return null;
  } catch {
    return null;
  }
}

/** The hub's reading of the preference: on until told otherwise. See `readMusicPreference`. */
export function isMusicEnabled(): boolean {
  return readMusicPreference() ?? true;
}

export function setMusicEnabled(enabled: boolean): void {
  try {
    window.localStorage.setItem(MUSIC_ENABLED_STORAGE_KEY, enabled ? 'on' : 'off');
  } catch {
    // A preference we cannot persist is still honoured for this session by the
    // caller's own state; failing to remember it is not worth breaking a click.
  }
}
